/**
 * Proje Genel Bakış.
 *
 * Rebuilt around the shared workflow read model. The screen used to derive its
 * own answer to "what should I do next?" from three separate requests - the
 * session list, the coverage analysis and the latest reconstruction - with the
 * rules inline in JSX. That produced the exact defect this rewrite removes:
 * the four-stage strip printed a hardcoded "3D Model - Henüz yok" while the
 * tile two columns to its right consulted `model_state.has_model`, so a
 * project with a validated mesh would have been described two ways on one
 * screen.
 *
 * Now there is **one request** for state - `GET /projects/{id}/workflow` -
 * evaluated by `domain/workflow.py`, the same code the library list runs. The
 * next action, the health checklist and the stage strip are three views of one
 * object, so they cannot disagree with each other or with a project card.
 *
 * ## Subject-aware, without inventing measurements
 *
 * The checklist marks head analysis and coverage `not_applicable` for the five
 * non-head subject types, in neutral tone with a sentence saying why. It is
 * not a blocker: the next action for a room project walks straight past
 * coverage to reconstruction, because a room is not held back by a head
 * measurement that does not exist for it.
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProjectWorkflow } from "@/api/client";
import { ErrorState } from "@/components/ErrorState";
import { IconCamera, IconCube, IconFiles, IconGrid, IconTarget } from "@/components/icons";
import { PageHeader, Section, SummaryTile, type Tone } from "@/components/product";
import { Button, Loading } from "@/components/ui";
import { GEOMETRY_MODE_TERMS, geometryModeLabel, subjectTypeLabel } from "@/i18n/terms";
import { useAsync } from "@/lib/useAsync";
import { paths } from "@/app/paths";
import {
  AttentionList,
  HealthChecklist,
  NextActionPanel,
} from "@/features/workflow/WorkflowViews";
import { ProjectTimeline } from "@/features/workflow/ProjectTimeline";
import { useProjectContext } from "./ProjectScreen";
import { DeleteProjectDialog, deletionNeedsTypedName } from "./DeleteProjectDialog";
import { EditProjectDialog } from "./EditProjectDialog";
import "./projects.css";
import "@/features/workflow/workflow.css";

/** A checklist state as a tile tone. `todo` is neutral; absence is not failure. */
const STAGE_TONE = (state: string | undefined): Tone =>
  state === "done" ? "success" : state === "attention" ? "caution" : "neutral";

export function ProjectOverviewTab() {
  const { project, reloadProject } = useProjectContext();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [editing, setEditing] = useState(false);

  const fetcher = useCallback(
    (signal: AbortSignal) => getProjectWorkflow(project.id, { signal }),
    [project.id],
  );
  const { state, reload } = useAsync(fetcher);

  if (state.status === "loading" && state.data === null) return <Loading />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={reload} />;
  if (state.data === null) return <Loading />;

  const workflow = state.data;
  const step = (key: string) => workflow.steps.find((item) => item.key === key);

  const media = step("media");
  const coverage = step("coverage");
  const sparse = step("sparse");
  const finalModel = step("final_model");

  return (
    <div className="stack">
      <PageHeader
        title={project.name}
        // Two facts, both stored, both decided by the customer: what this is
        // and what it is for. The second one changes what the pipeline is
        // allowed to do, so it belongs beside the first rather than buried in
        // an edit dialog nobody opens.
        subtitle={`${subjectTypeLabel(project.subject_type)} · ${geometryModeLabel(project.geometry_mode)}`}
        actions={
          <Button onClick={() => setEditing(true)}>Projeyi Düzenle</Button>
        }
      />

      {project.description && <p className="muted">{project.description}</p>}

      <p className="muted">{GEOMETRY_MODE_TERMS[project.geometry_mode ?? "precise"].modelNote}</p>

      <NextActionPanel workflow={workflow} projectId={project.id} />

      {/*
        Only when the backend produced at least one evidence-backed item. An
        always-present panel that must be filled is how a warning becomes
        wallpaper.
      */}
      {workflow.attention.length > 0 && (
        <Section title="Dikkat gerekiyor">
          <AttentionList items={workflow.attention} projectId={project.id} />
        </Section>
      )}

      {/*
        The four-stage strip, now reading the same checklist as everything
        else. The "3D Model" tile in particular is no longer hardcoded: it
        follows `final_model`, which is `done` only for a validated mesh.
      */}
      <div className="grid-4">
        <SummaryTile
          title="Çekim"
          icon={<IconCamera size={20} />}
          tone={STAGE_TONE(media?.state)}
          state={media?.state === "done" ? `${media.count ?? 0} dosya` : "Henüz yok"}
        />
        <SummaryTile
          title="Kapsama"
          icon={<IconTarget size={20} />}
          tone={STAGE_TONE(coverage?.state)}
          state={
            coverage?.state === "not_applicable"
              ? "Bu tür için ölçülmüyor"
              : coverage?.state === "done"
                ? "Yeterli"
                : coverage?.state === "attention"
                  ? "Ek çekim gerekiyor"
                  : "Henüz ölçülmedi"
          }
        />
        <SummaryTile
          title="Rekonstrüksiyon"
          icon={<IconGrid size={20} />}
          tone={STAGE_TONE(sparse?.state)}
          state={
            sparse?.state === "done"
              ? "Seyrek 3D hazır"
              : sparse?.state === "attention"
                ? "Başarısız"
                : "Henüz yok"
          }
        />
        <SummaryTile
          title="3D Model"
          icon={<IconCube size={20} />}
          tone={STAGE_TONE(finalModel?.state)}
          state={workflow.has_final_model ? "3D Model hazır" : "Henüz nihai model yok"}
        />
      </div>

      <div className="workspace">
        <div className="stack">
          <HealthChecklist workflow={workflow} subjectType={project.subject_type} />

          <Section title="Proje geçmişi">
            <ProjectTimeline projectId={project.id} />
          </Section>
        </div>

        <div className="stack">
          <SummaryTile
            title="Çekimler"
            icon={<IconCamera size={20} />}
            tone={STAGE_TONE(media?.state)}
            state={`${media?.count ?? 0} dosya`}
            onOpen={() => navigate(paths.projectSessions(project.id))}
          />
          <SummaryTile
            title="Kapsama"
            icon={<IconTarget size={20} />}
            tone={STAGE_TONE(coverage?.state)}
            state={
              coverage?.state === "not_applicable"
                ? "Görünüm dağılımı modeli"
                : coverage?.state === "done"
                  ? "Yeterli"
                  : "Henüz ölçülmedi"
            }
            onOpen={() => navigate(paths.projectCoverage(project.id))}
          />
          <SummaryTile
            title="3D Rekonstrüksiyon"
            icon={<IconGrid size={20} />}
            tone={STAGE_TONE(sparse?.state)}
            state={sparse?.state === "done" ? "Seyrek 3D hazır" : "Henüz oluşturulmadı"}
            onOpen={() => navigate(paths.projectReconstruction(project.id))}
          />
          <SummaryTile
            title="Dosyalar / Çıktılar"
            icon={<IconFiles size={20} />}
            tone={workflow.furthest_output ? "success" : "neutral"}
            state={
              workflow.furthest_output === "sparse"
                ? "Seyrek 3D çıktısı var"
                : workflow.furthest_output
                  ? "Çıktılar mevcut"
                  : "Henüz çıktı yok"
            }
            onOpen={() => navigate(paths.projectFiles(project.id))}
          />
        </div>
      </div>

      {/*
        Deletion lives at the very bottom, in its own section, away from every
        primary action on this screen. It is a real backend delete, so it is
        deliberately not one click from "Rekonstrüksiyona Git".
      */}
      <Section title="Tehlikeli işlemler">
        <div className="danger-zone__row">
          <div>
            <p style={{ margin: 0, fontWeight: 600 }}>Projeyi sil</p>
            <p className="muted" style={{ margin: "var(--sp-1) 0 0", fontSize: "0.88rem" }}>
              Proje, çekimleri ve yüklenen dosyaları kalıcı olarak silinir. Geri alınamaz.
            </p>
          </div>
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Projeyi Sil
          </Button>
        </div>
      </Section>

      {editing && (
        <EditProjectDialog
          project={project}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            // Re-read from the server rather than trusting the response body
            // alone: the header above this outlet renders the project, and it
            // must show what was stored, not what was sent.
            reloadProject();
          }}
        />
      )}

      {confirming && (
        <DeleteProjectDialog
          project={project}
          requireName={deletionNeedsTypedName(media?.count ?? 0)}
          onCancel={() => setConfirming(false)}
          onDeleted={() => navigate(paths.projects(), { replace: true })}
        />
      )}
    </div>
  );
}
